import { FeatureFlagKey, FeatureFlagVariationKey } from '@repo/types/feature-flag';
import { useCallback } from 'react';

import {
  findLocaleFromUrlAndCookie,
  findSiteByDomain,
  findSiteFromUrl,
  useSiteContext,
} from '@zooplus/commons-lang';

import {
  Component,
  Platform,
  trackEvent,
  TRACKING_EVENT,
  TRACKING_PAGE,
  TrackingPage,
} from './tracker-listener';
/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    MSStream: any;
    opera: string;
  }
}

export function useTracking() {
  const { site, locale } = useSiteAutoInfo();

  const getAutomaticTrackDetails = useCallback(() => {
    return {
      domain: site.domain,
      siteId: site.id,
      locale: locale,
      currencyCode: site.alternativeCurrency,
      pageLocation: window.location.href,
      platform: getPlatform(),
    };
  }, [site, locale]);

  const trackPageView = useCallback(
    ({ page }: { page?: TrackingPage } = {}) => {
      trackEvent({
        event: TRACKING_EVENT.PAGE_VIEW,
        page: page ?? getTrackingPage(),
        detail: {
          ...getAutomaticTrackDetails(),
        },
      });
    },
    [getAutomaticTrackDetails],
  );

  const trackClick = useCallback(
    ({ page, target }: { page?: TrackingPage; target?: Component }) => {
      trackEvent({
        event: 'CLICK',
        page: page ?? getTrackingPage(),
        detail: {
          ...getAutomaticTrackDetails(),
          target: target ?? { name: '', id: '' },
        },
      });
    },
    [getAutomaticTrackDetails],
  );

  const trackInteraction = useCallback(
    ({ page: page, components }: { page?: TrackingPage; components: Component[] }) => {
      trackEvent({
        event: 'INTERACTION',
        page: page ?? getTrackingPage(),
        detail: {
          ...getAutomaticTrackDetails(),
          components: components,
        },
      });
    },
    [getAutomaticTrackDetails],
  );

  const trackExperiment = useCallback(
    ({
      featureKey,
      variationKey,
    }: {
      featureKey: FeatureFlagKey;
      variationKey: FeatureFlagVariationKey;
    }) => {
      trackEvent({
        event: TRACKING_EVENT.EXPERIMENT,
        page: getTrackingPage(),
        detail: {
          ...getAutomaticTrackDetails(),
          experiments: [
            {
              tool: 'OPTIMIZELY',
              id: featureKey,
              value: variationKey,
            },
          ],
        },
      });
    },
    [getAutomaticTrackDetails],
  );

  return {
    trackPageView,
    trackClick,
    trackInteraction,
    trackExperiment,
  };
}

/**
 * Returns site and locale information automatically from the SiteContext when available,
 * falls back to retrieve the site information from the URL when the context is not available.
 */
function useSiteAutoInfo() {
  const siteContext = useSiteContext();

  if (siteContext?.site && siteContext?.locale) {
    return {
      site: siteContext.site,
      locale: siteContext.locale,
    };
  }

  // fallback to find the site from the URL in case the context is not available.
  // Ultimately falls back to hardcoded zooplus.com (although we should always be able to find the site from the URL in normal situations).
  const site = findSiteFromUrl() ?? findSiteByDomain('zooplus.com');

  return {
    site: site,
    locale: findLocaleFromUrlAndCookie() ?? site.locale,
  };
}

function getPlatform(): Platform {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'IOS';
  }

  if (/android/i.test(userAgent)) {
    return 'ANDROID';
  }

  return 'WEB';
}

function getTrackingPage(): TrackingPage {
  const path = window.location.href;

  //switch case for different paths
  switch (true) {
    case path.includes('/checkout/cart'):
      return TRACKING_PAGE.CHECKOUT_CART;
    case path.includes('/checkout/preview'):
      return TRACKING_PAGE.CHECKOUT_PREVIEW;
    case path.includes('/checkout/finish'):
      return TRACKING_PAGE.CHECKOUT_FINISH;
    case path.includes('/zooclub/dashboard'):
      return TRACKING_PAGE.LOYALTY_PROGRAM_DASHBOARD;
    case path.includes('/zooclub/overview'):
      return TRACKING_PAGE.LOYALTY_PROGRAM_LANDING_PAGE;
    case path.includes('/account/overview'):
      return TRACKING_PAGE.ACCOUNT;
    default:
      return TRACKING_PAGE['PRODUCT-DETAIL'];
  }
}
